/**
 * MGL Search Module
 * Client-side search utility for SpatchAI PWA
 * Handles searching/filtering Massachusetts General Laws
 * 
 * Uses optimized compact index files:
 * - mgl-index-optimized.json: chapter metadata (19KB)
 * - mgl-citations.json: citation lookup table (3MB)
 */

import type {
  MGLIndexMeta,
  MGLChapter,
  MGLCitationsMap,
  MGLSection
} from '@/types/statute';

interface ChapterListItem {
  number: string;
  name: string;
  sectionCount: number;
}

interface SearchResult {
  chapter: string;
  section: string;
  heading: string;
  url: string;
  citation: string;
}

interface MetadataResult {
  version: number;
  generated?: string;
  totalChapters?: number;
  totalSections?: number;
}

export class MGLSearch {
  private index: MGLIndexMeta;
  /** Citation lookup map; read by the statute-lookup feature. */
  readonly citations: MGLCitationsMap;
  private cache: Map<string, MGLChapter>;

  constructor(indexData: MGLIndexMeta, citationsData?: { d: MGLCitationsMap }) {
    this.index = indexData;
    this.citations = citationsData?.d || {};
    this.cache = new Map();
  }

  /**
   * Load index from files
   */
  static async load(
    indexPath: string = 'data/mgl-index-optimized.json',
    citationsPath: string = 'data/mgl-citations.json'
  ): Promise<MGLSearch | null> {
    try {
      const [indexResp, citResp] = await Promise.all([
        fetch(indexPath),
        fetch(citationsPath)
      ]);

      if (!indexResp.ok || !citResp.ok) {
        throw new Error(`HTTP error: ${indexResp.status}, ${citResp.status}`);
      }

      const index: MGLIndexMeta = await indexResp.json();
      const citations: { d: MGLCitationsMap } = await citResp.json();

      return new MGLSearch(index, citations);
    } catch (error) {
      console.error('Failed to load MGL index:', error);
      return null;
    }
  }

  /**
   * Search by citation (e.g., "G.L. c. 149, § 150")
   */
  searchByCitation(query: string): SearchResult | null {
    // Normalize while preserving "c." abbreviation format
    const normalized = query
      .replace(/M\.G\.L\.|MGL/gi, 'G.L.') // Normalize GL variants
      .replace(/\s+/g, ' ') // Collapse whitespace
      .replace(/[Cc]hapter/gi, 'c.') // Expand "Chapter" to "c."
      .replace(/[Ss]ection|§/g, '§') // Normalize section symbol
      .toUpperCase() // Uppercase everything except the "c." in "G.L. c."
      .replace(/G\.L\. C\./g, 'G.L. c.') // Fix: keep "c." lowercase
      .trim();

    const result = this.citations[normalized];
    if (result) {
      return {
        chapter: result.c,
        section: result.s,
        heading: result.h,
        url: result.u,
        citation: normalized
      };
    }
    return null;
  }

  /**
   * Search by keyword in chapter names
   * Note: Full-text section search requires loading the chapter file
   */
  searchByKeyword(keyword: string): ChapterListItem[] {
    const query = keyword.toLowerCase();
    const chapters = this.index?.c || {};

    const results: ChapterListItem[] = [];
    Object.entries(chapters).forEach(([chNum, chData]) => {
      if (chData.n.toLowerCase().includes(query)) {
        results.push({
          number: chNum,
          name: chData.n,
          sectionCount: chData.c
        });
      }
    });

    return results;
  }

  /**
   * Get a specific chapter
   */
  async getChapter(chapterNum: string): Promise<MGLChapter | null> {
    if (this.cache.has(chapterNum)) {
      return this.cache.get(chapterNum) || null;
    }

    try {
      const response = await fetch(`data/mgl/chapter-${chapterNum}.json`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data: MGLChapter = await response.json();
      this.cache.set(chapterNum, data);
      return data;
    } catch (error) {
      console.error(`Failed to load chapter ${chapterNum}:`, error);
      return null;
    }
  }

  /**
   * Get a specific section with full text
   */
  async getSection(
    chapterNum: string,
    sectionNum: string
  ): Promise<MGLSection | null> {
    const chapter = await this.getChapter(chapterNum);
    if (!chapter || !chapter.sections) return null;

    return chapter.sections.find((s) => s.section === sectionNum) || null;
  }

  /**
   * Advanced search with multiple filters
   */
  search(query: string): SearchResult[] | ChapterListItem[] {
    // Try citation first
    const byCitation = this.searchByCitation(query);
    if (byCitation) return [byCitation];

    // Fall back to keyword search
    return this.searchByKeyword(query);
  }

  /**
   * Get all chapters
   */
  getChapters(): ChapterListItem[] {
    const chapters = this.index?.c || {};
    return Object.entries(chapters).map(([num, data]) => ({
      number: num,
      name: data.n,
      sectionCount: data.c
    }));
  }

  /**
   * Get metadata
   */
  getMetadata(): MetadataResult {
    return {
      version: this.index?.v || 1,
      generated: this.index?.g,
      totalChapters: this.index?.m?.tc,
      totalSections: this.index?.m?.ts
    };
  }

  /**
   * Clear cache to save memory on mobile
   */
  clearCache(): void {
    this.cache.clear();
  }
}

export default MGLSearch;
